import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { PhoneFrame, BrandAvatar } from './styles';
import { hasMediaContent, isImage, isVideo, toHandle, truncate } from './utils';
import { XReplyIcon, XRetweetIcon, XLikeIcon, XViewsIcon, XShareIcon } from './PlatformIcons';
import MediaSlide from './MediaSlide';

const X_FONT = '"TwitterChirp", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const TRUNCATE_AT = 280;
const TEXT_GREY = '#536471';

const Card = styled(PhoneFrame)({
    fontFamily: X_FONT,
    color: '#0f1419',
    padding: '12px 16px',
});

const Row = styled('div')({ display: 'flex', gap: 12 });

const Column = styled('div')({ flex: 1, minWidth: 0 });

const NameRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 15,
    lineHeight: '20px',
});

const DisplayName = styled('span')({
    fontWeight: 700,
    color: '#0f1419',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 180,
});

const Handle = styled('span')({
    color: TEXT_GREY,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const Dot = styled('span')({ color: TEXT_GREY });

const More = styled(MoreHorizIcon)({ color: TEXT_GREY, marginLeft: 'auto', fontSize: 18 });

const Body = styled('div')({
    fontSize: 15,
    lineHeight: '20px',
    color: '#0f1419',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginTop: 4,
});

const MediaWrap = styled('div')({
    marginTop: 12,
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid #cfd9de',
});

const Grid = styled('div', { shouldForwardProp: p => p !== 'count' })(({ count }) => ({
    display: 'grid',
    gap: 2,
    aspectRatio: '16 / 9',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: count > 2 ? '1fr 1fr' : '1fr',
    ...(count === 3 && { gridTemplateAreas: '"a b" "a c"' }),
}));

const Tile = styled('img', { shouldForwardProp: p => p !== 'area' })(({ area }) => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    ...(area && { gridArea: area }),
}));

const ActionsRow = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: TEXT_GREY,
    marginTop: 12,
});

const Action = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    '& svg': { fontSize: 18 },
});

const XMedia = ({ media }) => {
    const items = useMemo(
        () => (Array.isArray(media) ? media : []).filter(m => m?.link && (isImage(m) || isVideo(m))),
        [media],
    );

    if (items.length === 0) return null;

    // X never carousels and forbids mixed media: a lone item or a video renders on its own.
    if (items.length === 1 || isVideo(items[0])) {
        return (
            <MediaWrap>
                <MediaSlide media={[items[0]]} aspectRatio="16 / 9" />
            </MediaWrap>
        );
    }

    const images = items.filter(isImage).slice(0, 4);
    const areas = images.length === 3 ? ['a', 'b', 'c'] : [];

    return (
        <MediaWrap>
            <Grid count={images.length}>
                {images.map((item, i) => (
                    <Tile key={`${item.link}-${i}`} src={item.link} alt="" area={areas[i]} />
                ))}
            </Grid>
        </MediaWrap>
    );
};

const PlatformCardX = ({ pageName, handle: realHandle, locationLogo, message, media, dateLabel }) => {
    const handle = realHandle || toHandle(pageName, 24);
    const { shown } = truncate(message, TRUNCATE_AT);

    return (
        <Card>
            <Row>
                <BrandAvatar name={pageName} image={locationLogo} size={40} color="#0f1419" />
                <Column>
                    <NameRow>
                        <DisplayName>{pageName}</DisplayName>
                        <Handle>@{handle}</Handle>
                        {dateLabel && <Dot>·</Dot>}
                        {dateLabel && <Handle>{dateLabel}</Handle>}
                        <More />
                    </NameRow>

                    {message && <Body>{shown}</Body>}

                    {hasMediaContent(media) && <XMedia media={media} />}

                    <ActionsRow>
                        <Action>
                            <XReplyIcon />
                        </Action>
                        <Action>
                            <XRetweetIcon />
                        </Action>
                        <Action>
                            <XLikeIcon />
                        </Action>
                        <Action>
                            <XViewsIcon />
                        </Action>
                        <Action>
                            <XShareIcon />
                        </Action>
                    </ActionsRow>
                </Column>
            </Row>
        </Card>
    );
};

export default PlatformCardX;
